export const SOLUTION_DETAILS = {
  ehr: {
    key: "ehr", icon: "FileText", name: "Unified EHR / EMR",
    tagline: "A connected record for a more complete picture of care.",
    heroBlurb: "One longitudinal patient record across every division, setting and care team \u2014 so the right information is always where care happens.",
    image: "/assets/hero-tablet.png",
    overview: [
      "Proactive UMS unifies clinical documentation into a single, connected electronic health record that follows the patient across primary care, behavioral health, home care and specialty settings.",
      "Built on open standards, our EHR reduces duplicate work, closes information gaps and gives clinicians a complete, real-time picture of every patient.",
    ],
    features: [
      { icon: "FileText", title: "Longitudinal Record", text: "A single chart spanning every encounter and division." },
      { icon: "Stethoscope", title: "Clinical Documentation", text: "Fast, structured notes designed around real workflows." },
      { icon: "Share2", title: "Interoperability", text: "FHIR/HL7 exchange with labs, pharmacies and partners." },
      { icon: "ShieldCheck", title: "Privacy & Security", text: "HIPAA-ready access controls and audit trails." },
      { icon: "Workflow", title: "Orders & Results", text: "Integrated e-prescribing, orders and results routing." },
      { icon: "Activity", title: "Care Gaps & Alerts", text: "Real-time reminders that keep care on track." },
    ],
    benefits: ["One patient, one connected record", "Fewer duplicate tests and errors", "Faster, structured documentation", "Standards-based interoperability", "Role-based security & audit trails", "Real-time clinical decision support"],
    stats: [{ value: "1", label: "Unified Record" }, { value: "FHIR/HL7", label: "Interoperable" }, { value: "HIPAA-Ready", label: "Security" }],
  },
  rcm: {
    key: "rcm", icon: "Settings", name: "Practice & RCM",
    tagline: "Streamlined workflows. Healthier revenue.",
    heroBlurb: "Practice management and revenue cycle tools that streamline operations and strengthen financial performance.",
    image: "/assets/hero-slide-4.png",
    overview: [
      "From scheduling to claims, Proactive UMS streamlines the day-to-day operations of your practice while optimizing every step of the revenue cycle.",
      "Automation reduces administrative burden, accelerates reimbursement and gives leaders clear visibility into financial performance.",
    ],
    features: [
      { icon: "Settings", title: "Practice Management", text: "Scheduling, registration and front-office workflows." },
      { icon: "BarChart3", title: "Revenue Cycle", text: "Claims, coding, billing and denials management." },
      { icon: "LineChart", title: "Financial Insights", text: "Dashboards for revenue, AR and productivity." },
      { icon: "Workflow", title: "Automation", text: "Automated eligibility, reminders and follow-ups." },
      { icon: "ShieldCheck", title: "Compliance", text: "Coding and billing aligned to regulations." },
      { icon: "Users", title: "Payer Connectivity", text: "Streamlined submission and remittance." },
    ],
    benefits: ["Faster, cleaner claims", "Fewer denials and rework", "Improved cash flow", "Less administrative burden", "Clear financial visibility", "Compliant coding & billing"],
    stats: [{ value: "Faster", label: "Reimbursement" }, { value: "Fewer", label: "Denials" }, { value: "Clear", label: "Financial Visibility" }],
  },
  integration: {
    key: "integration", icon: "Workflow", name: "Enterprise Integration",
    tagline: "Connect systems, people and data.",
    heroBlurb: "Standards-based integration that connects your systems, partners and data into one coordinated ecosystem.",
    image: "/assets/hero-slide-1.png",
    overview: [
      "Proactive UMS connects EHRs, devices, payers and partner systems using open standards \u2014 eliminating silos and enabling coordinated, data-driven care.",
      "Our integration engine keeps information flowing securely and reliably across the entire ecosystem.",
    ],
    features: [
      { icon: "Workflow", title: "Integration Engine", text: "Reliable, scalable interface and event routing." },
      { icon: "Share2", title: "FHIR / HL7 APIs", text: "Standards-based connectivity with any system." },
      { icon: "Database", title: "Data Exchange", text: "Secure, real-time data across partners." },
      { icon: "Cpu", title: "Device Connectivity", text: "Connect medical devices into workflows." },
      { icon: "Lock", title: "Secure by Design", text: "Encrypted, governed data movement." },
      { icon: "Boxes", title: "Partner Ecosystem", text: "Onboard payers, labs and vendors quickly." },
    ],
    benefits: ["No more data silos", "Real-time information flow", "Standards-based & future-proof", "Secure, governed exchange", "Faster partner onboarding", "Scales with your organization"],
    stats: [{ value: "FHIR/HL7", label: "Standards" }, { value: "Real-time", label: "Data Flow" }, { value: "Secure", label: "By Design" }],
  },
  portals: {
    key: "portals", icon: "Users", name: "Patient / Provider / Employer Portals",
    tagline: "Access. Engagement. Better outcomes.",
    heroBlurb: "Connected portals that give patients, providers and employers the access and insight they need to engage in better care.",
    image: "/assets/hero-child-nurse.png",
    overview: [
      "Proactive UMS delivers secure, intuitive portals tailored to patients, providers and employers \u2014 putting the right tools and information in every stakeholder's hands.",
      "Greater access drives engagement, and engagement drives better outcomes across the board.",
    ],
    features: [
      { icon: "Heart", title: "Patient Portal", text: "Appointments, records, messaging and telehealth." },
      { icon: "Stethoscope", title: "Provider Portal", text: "Referrals, results and coordinated care tools." },
      { icon: "Briefcase", title: "Employer Portal", text: "Population insights for health plan sponsors." },
      { icon: "Video", title: "Secure Messaging", text: "HIPAA-ready communication and virtual visits." },
      { icon: "FileText", title: "Records Access", text: "Patients own and share their health data." },
      { icon: "Activity", title: "Engagement Tools", text: "Reminders, education and self-service." },
    ],
    benefits: ["Self-service access anytime", "Stronger patient engagement", "Coordinated provider workflows", "Actionable employer insights", "Secure messaging & sharing", "Better experiences and outcomes"],
    stats: [{ value: "3", label: "Portal Experiences" }, { value: "24/7", label: "Secure Access" }, { value: "HIPAA-Ready", label: "Privacy" }],
  },
  telehealth: {
    key: "telehealth", icon: "MonitorSmartphone", name: "Telehealth & Remote Care",
    tagline: "Care without boundaries.",
    heroBlurb: "Virtual visits and remote monitoring that extend care beyond the clinic \u2014 wherever patients are.",
    image: "/assets/hero-slide-3.png",
    overview: [
      "Proactive UMS makes care accessible everywhere with integrated telehealth and remote patient monitoring, connected directly to the clinical record.",
      "Patients get convenient, continuous care while clinicians stay informed with real-time data.",
    ],
    features: [
      { icon: "Video", title: "Video Visits", text: "Secure, high-quality virtual consultations." },
      { icon: "Activity", title: "Remote Monitoring", text: "Connected devices stream vitals in real time." },
      { icon: "MonitorSmartphone", title: "Virtual Care Anywhere", text: "Care from home, work or on the go." },
      { icon: "HeartPulse", title: "Chronic Care", text: "Continuous monitoring for complex conditions." },
      { icon: "Workflow", title: "Integrated Workflows", text: "Telehealth built into the clinical record." },
      { icon: "ShieldCheck", title: "Secure & Compliant", text: "HIPAA-ready virtual care." },
    ],
    benefits: ["Care from anywhere", "Fewer avoidable visits", "Continuous remote monitoring", "Better access for rural & mobility-limited", "Integrated with the EHR", "Higher patient satisfaction"],
    stats: [{ value: "Anywhere", label: "Virtual Care" }, { value: "24/7", label: "Remote Monitoring" }, { value: "Connected", label: "To the Record" }],
  },
  analytics: {
    key: "analytics", icon: "BarChart3", name: "Analytics & Automation",
    tagline: "Turn data into healthier decisions.",
    heroBlurb: "AI-driven analytics and automation that turn data into insight \u2014 and insight into healthier decisions.",
    image: "/assets/hero-slide-4.png",
    overview: [
      "Proactive UMS unifies clinical, operational and financial data into powerful analytics \u2014 surfacing the insights leaders need to act with confidence.",
      "Intelligent automation then puts those insights to work, streamlining operations and improving outcomes.",
    ],
    features: [
      { icon: "LineChart", title: "Analytics Dashboards", text: "Clinical, operational and financial insight." },
      { icon: "Cpu", title: "AI & Prediction", text: "Predictive models that anticipate needs." },
      { icon: "Workflow", title: "Process Automation", text: "Automate routine, repetitive workflows." },
      { icon: "Database", title: "Unified Data", text: "One connected data foundation." },
      { icon: "Activity", title: "Population Health", text: "Identify risk and close care gaps." },
      { icon: "ShieldCheck", title: "Governed & Secure", text: "Trusted, compliant data at every step." },
    ],
    benefits: ["One source of truth", "Predictive, proactive insight", "Automated routine work", "Closed care gaps", "Better resource decisions", "Measurable outcomes"],
    stats: [{ value: "AI-Driven", label: "Insights" }, { value: "Automated", label: "Workflows" }, { value: "Unified", label: "Data" }],
  },
};
